import { Link } from 'react-router-dom';
import { Calendar, Clock, User } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BlogPost } from '@/types/blog';
import { formatBlogDate } from '@/utils/dateFormat';
import { useLanguage } from '@/hooks/useLanguage';
import { cn } from '@/lib/utils';
import { OptimizedImage } from '@/components/ui/OptimizedImage';

interface BlogCardProps {
  post: BlogPost;
  className?: string;
  featured?: boolean;
}

export function BlogCard({ post, className, featured = false }: BlogCardProps) {
  const { t, currentLanguage } = useLanguage();

  return (
    <article className={cn(
      "bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group",
      featured && "md:col-span-2 md:grid md:grid-cols-2",
      className
    )}>
      {/* Image */}
      <div className={cn(
        "relative overflow-hidden",
        featured ? "h-64 md:h-full" : "h-48"
      )}>
        <OptimizedImage
          src={post.featuredImage}
          alt={post.title}
          className="w-full h-full group-hover:scale-105 transition-transform duration-300"
          objectFit="cover"
          priority={featured}
        />
        
        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <Badge variant="secondary" className="bg-white/90 text-gray-900">
            {post.category}
          </Badge>
        </div>

        {featured && post.featured && (
          <div className="absolute top-3 right-3">
            <Badge variant="destructive">
              DESTACADO
            </Badge>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col justify-between h-full">
        <div>
          <h3 className={cn(
            "font-bold mb-3 group-hover:text-blue-600 transition-colors line-clamp-2",
            featured ? "text-xl md:text-2xl" : "text-lg"
          )}>
            <Link to={`/blog/${post.slug}`}>
              {post.title}
            </Link>
          </h3>

          <p className={cn(
            "text-gray-600 mb-4 line-clamp-3",
            featured ? "text-base" : "text-sm"
          )}>
            {post.excerpt}
          </p>
        </div>

        {/* Meta Information */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              <span>{formatBlogDate(post.publishedAt)}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-1" />
              <span>{post.readTime} {t('blog.readTime')}</span>
            </div>
            {post.author.name && (
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                <span>{post.author.name}</span>
              </div>
            )}
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.tags.slice(0, 3).map((tag) => (
                <Badge key={tag} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          <div className="pt-2">
            <Link
              to={`/blog/${post.slug}`}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
            >
              {t('blog.readMore')} →
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}